import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAuth } from "../../context/AuthContext";
import { ChangeEvent, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApolloError, useMutation } from "@apollo/client";
import { CREATE_EMPLOYEE } from "../../api/mutation";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import BreadCumb from "../../components/common/Breadcrumb";
import InputsNew from "../../components/common/InputsNew";
import backArrow from "../../assets/task/back_arrow.svg";
import Loader from "../../components/common/Loader";
import { uploadFileDirectly } from "../projects/AddProject";
import plusIconuplad from "../../assets/task/plus_icon_uload.svg";
import imgUploadicon from "../../assets/task/imguploadicon.svg";

interface AddEmployeeConfig {
    firstName: string;
    lastName: string;
    email: string;
    mobileNo: string;
    imageurl?: string;
}

const addEmployeeSchema = yup.object().shape({
    firstName: yup.string()
        .test('not-only-whitespace', 'Please add a valid First Name', value => !value || value.trim().length > 0)
        .required('This field is required'),
    lastName: yup.string()
        .test('not-only-whitespace', 'Please add a valid Last Name', value => !value || value.trim().length > 0)
        .required('This field is required'),
    mobileNo: yup.string()
        .required('This field is required')
        .transform(value => value.replace(/[^0-9]/g, ''))
        .matches(/^\d{10}$/, 'Please add a valid Mobile Number'),
    email: yup.string().required('This field is required').matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/, 'Please enter a valid Email Address'),
    imageurl: yup.string()
});


const AddEmployee = () => {
    const navigate = useNavigate()

    const { userInfo } = useAuth()
    const employeeID = userInfo?.employee_id || '';
    const organizationID = userInfo?.agencies?.id || '';
    const employeeName = `${userInfo?.first_name} ${userInfo?.last_name}`

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [imgUrl, setImgUrl] = useState("");
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string | null>(
        null
    );

    // react hook form
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm<any>({
        resolver: yupResolver(addEmployeeSchema)
    });

    //api
    const [createEmployee, { loading: createEmployeeLoading }] = useMutation(CREATE_EMPLOYEE, {
        fetchPolicy: 'no-cache',
    })

    function handleIconClick() {
        fileInputRef.current?.click();
    }

    async function handleFileUpload(event: ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        // if (!["image/jpeg", "image/png"].includes(file.type)) {
        //     setValidationMessage("Only JPG and PNG files are allowed.");
        //     return;
        // }

        setValidationMessage(null);
        setIsUploading(true);

        try {
            const token = localStorage.getItem("authToken")!;
            const url = await uploadFileDirectly(file, file.name, token);
            setImgUrl(url);
            setValue("imageurl", url);
        } catch (err) {
            console.error("Upload failed", err);
        } finally {
            setIsUploading(false);
        }
    }

    async function onSubmit(data: AddEmployeeConfig) {
        const input = {
            first_name: data.firstName,
            last_name: data.lastName,
            email_id: data.email,
            contact_no: data.mobileNo,
            agency_id: parseInt(organizationID),
            image_url: data.imageurl,
        }

        try {
            const response = await createEmployee({ variables: { input: input } });
            if (response.data.create_employee.status_code === 201) {
                showSuccess(response.data.create_employee.message)
                navigate(-1)
            }
            if (response.data.create_employee.status_code === 400) {
                showError(response.data.create_employee.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} id="cts-respon-p-dv" className="w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb />
                    {/* <div className="self-stretch rounded flex flex-col items-start justify-start text-dimgray">
                                <div className="flex flex-row items-center justify-start">
                                    <div className="overflow-hidden flex flex-row items-center justify-start gap-2">
                                        <div className="relative [text-decoration:underline]">Dashboard</div>
                                        <img className="w-4 relative h-4 overflow-hidden shrink-0" alt="" src="Right 16px.svg" />
                                        <div className="relative font-semibold text-gray">Add New Task</div>
                                    </div>
                                </div>
                            </div> */}
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Add Employee</h3>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                        {/* <h5 className="my-0 text-xl-dv self-stretch relative text-lg">Goal Details</h5> */}
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <InputsNew
                                contentPlaceholder="First Name"
                                label="First Name*"
                                type="text"
                                register={register('firstName')}
                                errorMessage={errors.firstName}
                            />
                            <InputsNew
                                contentPlaceholder="Last Name"
                                label="Last Name*"
                                type="text"
                                register={register('lastName')}
                                errorMessage={errors.lastName}
                            />
                            <InputsNew
                                label="Email Address*"
                                contentPlaceholder="Email Address"
                                type="text"
                                register={register('email', {
                                    onChange: (e) => {
                                        e.target.value = e.target.value.toLowerCase(); // Convert to lowercase while typing
                                    },
                                })}
                                errorMessage={errors.email}
                            />
                            <InputsNew
                                label="Mobile Number*"
                                contentPlaceholder="Mobile Number"
                                type="number"
                                register={register('mobileNo')}
                                errorMessage={errors.mobileNo}
                            />
                        </div>
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-4 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                            <div className=" rounded flex flex-col items-center justify-start text-left mq1275:min-w-[20%] mq1275:justify-start min-w-[22%]">
                                <label className="self-stretch relative font-semibold dark:text-white pb-2">Photo Upload</label>
                                <div
                                    className={`${imgUrl ? "upload_img_box" : ""} rounded  mq1275:bg-white flex flex-col items-center justify-end p-2.5 box-border max-w-[100%] min-w-[100%] mq950:min-w-full text-center agencyinfo-img-blg mq950:p-0`} id="calltype-row "
                                >
                                    {isUploading ? (
                                        <div className="self-stretch rounded bg-white flex flex-col items-center justify-center py-[33px] px-[9px] border-[1px] border-dashed border-neutral-200">
                                            <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                                                <div className="text-main-blue">Uploading...</div>
                                            </div>
                                        </div>
                                    ) : imgUrl ? (
                                        <div className="bg-white p-2 shadow-\[0px_1px_2px_rgba\(16\2c _24\2c _40\2c _0\.05\)\] border-surface-border   inner_upload_img_box onborading-bdr-blg">
                                            <img
                                                className="min-w-36 h-[100px] relative overflow-hidden shrink-0 cursor-pointer imgagency-upload"
                                                loading="lazy"
                                                alt=""
                                                src={imgUrl}
                                                onClick={handleIconClick}
                                            />
                                        </div>
                                    ) : (
                                        <div className="self-stretch rounded bg-white flex flex-col items-center justify-center py-[35px] px-[9px] border-[1px] border-dashed border-neutral-200">
                                            <div className="self-stretch flex flex-col items-center justify-center gap-[10px]">
                                                <img
                                                    className="w-6 h-6 relative overflow-hidden shrink-0 dark-white-img"
                                                    loading="lazy"
                                                    alt=""
                                                    src={imgUploadicon}
                                                />
                                                <p className="self-stretch relative capitalize my-0">
                                                    Photo Upload
                                                </p>
                                                <div
                                                    className="cursor-pointer rounded plus-task-btn flex flex-row items-center justify-center p-1.5"
                                                    onClick={handleIconClick}
                                                >
                                                    <img
                                                        className="h-5 w-5 relative"
                                                        loading="lazy"
                                                        alt=""
                                                        src={plusIconuplad}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload}
                                    accept=".jpg,.jpeg,.png"
                                // accept="image/*"
                                />
                                {validationMessage && (
                                    <p className="text-red p-2 cts-bg-error my-0 flex items-center gap-[4px] ctcerror-width-blg">
                                        <img src={''} alt="" />
                                        {validationMessage}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border">
                            <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                // onClick={handleGoBack}
                                type="button"
                                onClick={() => navigate(-1)}
                            >
                                <div className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">
                                    Cancel
                                </div>
                            </button>
                            <button className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                                // disabled={(err || careDelErr) !== ''}
                                type="submit"
                            >
                                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                    Save
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </form >
            {(createEmployeeLoading) && (
                <Loader />
            )}
        </>
    )
}

export default AddEmployee